// Admin view for troubleshooting PayFast ITN webhook events.
//
// Surfaces every PayFast event recorded in `payment_events` and tags it
// with the diagnostic category that matters when chasing duplicate
// credit orders: signature validation result, replay detection (the
// stable `external_event_id` collision), source-IP rejection, rate
// limiting, idempotent duplicates, and successful processing.
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { adminListPaymentEvents } from "@/lib/api/payments-admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "@tanstack/react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  AlertTriangle,
  Copy,
  XCircle,
  Repeat2,
  Gauge,
} from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/payfast-itn",
)({
  component: PayfastItnAdmin,
});

type Bucket =
  | "signature_invalid"
  | "validate_failed"
  | "ip_rejected"
  | "rate_limited"
  | "duplicate"
  | "unmatched"
  | "processed"
  | "other";

function classifyEvent(row: any): Bucket {
  const t = String(row.event_type ?? "").toLowerCase();
  const err = String(row.error_message ?? "").toLowerCase();
  if (t.includes("ip_rejected") || err.includes("untrusted source ip"))
    return "ip_rejected";
  if (t.includes("rate")) return "rate_limited";
  if (t.includes("signature") || err.includes("signature"))
    return "signature_invalid";
  if (t.includes("validate") || err.includes("validate"))
    return "validate_failed";
  if (t.includes("duplicate") || err.includes("duplicate")) return "duplicate";
  if (t.includes("unmatched") || err.includes("not found")) return "unmatched";
  if (row.processed) return "processed";
  return "other";
}

const BUCKETS: Record<
  Bucket,
  { label: string; icon: any; tone: string }
> = {
  processed: {
    label: "Processed",
    icon: CheckCircle2,
    tone: "bg-emerald-100 text-emerald-800",
  },
  signature_invalid: {
    label: "Signature invalid",
    icon: ShieldAlert,
    tone: "bg-rose-100 text-rose-800",
  },
  validate_failed: {
    label: "Validate failed",
    icon: XCircle,
    tone: "bg-rose-100 text-rose-800",
  },
  ip_rejected: {
    label: "Untrusted IP",
    icon: ShieldCheck,
    tone: "bg-amber-100 text-amber-900",
  },
  rate_limited: {
    label: "Rate limited",
    icon: Gauge,
    tone: "bg-amber-100 text-amber-900",
  },
  duplicate: {
    label: "Duplicate (idempotent)",
    icon: Repeat2,
    tone: "bg-slate-200 text-slate-800",
  },
  unmatched: {
    label: "Unmatched reference",
    icon: AlertTriangle,
    tone: "bg-amber-100 text-amber-900",
  },
  other: {
    label: "Other",
    icon: AlertTriangle,
    tone: "bg-slate-100 text-slate-700",
  },
};

function CategoryPill({ bucket }: { bucket: Bucket }) {
  const meta = BUCKETS[bucket];
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.tone}`}
    >
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function PayfastItnAdmin() {
  const list = useServerFn(adminListPaymentEvents);
  const [reference, setReference] = useState("");
  const [category, setCategory] = useState<"all" | Bucket>("all");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["payfast-itn-events", reference, page],
    queryFn: () =>
      list({
        data: {
          reference: reference || undefined,
          provider: "payfast",
          processed: "all",
          page,
          pageSize,
        },
      }),
  });

  const rows = useMemo(() => {
    const all = (data?.rows ?? []).map((r: any) => ({
      ...r,
      _bucket: classifyEvent(r) as Bucket,
    }));
    if (category === "all") return all;
    return all.filter((r) => r._bucket === category);
  }, [data, category]);

  const summary = useMemo(() => {
    const counts: Record<Bucket, number> = {
      processed: 0,
      signature_invalid: 0,
      validate_failed: 0,
      ip_rejected: 0,
      rate_limited: 0,
      duplicate: 0,
      unmatched: 0,
      other: 0,
    };
    for (const r of data?.rows ?? []) counts[classifyEvent(r) as Bucket]++;
    return counts;
  }, [data]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy">PayFast ITN events</h1>
          <p className="text-sm text-muted-foreground">
            Signature, replay, rate-limit and idempotency diagnostics for
            troubleshooting duplicate credit orders.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/portal/admin/payment-events">All payment events →</Link>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />{" "}
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </header>

      {/* Diagnostic summary */}
      <section className="grid gap-2 sm:grid-cols-4">
        {(
          [
            "processed",
            "signature_invalid",
            "validate_failed",
            "ip_rejected",
            "rate_limited",
            "duplicate",
            "unmatched",
            "other",
          ] as Bucket[]
        ).map((b) => (
          <button
            key={b}
            onClick={() => setCategory((c) => (c === b ? "all" : b))}
            className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left transition ${
              category === b
                ? "border-navy bg-navy/5"
                : "border-border hover:bg-muted/40"
            }`}
          >
            <CategoryPill bucket={b} />
            <span className="font-mono text-sm font-semibold text-navy">
              {summary[b]}
            </span>
          </button>
        ))}
      </section>

      {/* Filters */}
      <section className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs">Reference contains</Label>
          <Input
            value={reference}
            onChange={(e) => {
              setReference(e.target.value);
              setPage(1);
            }}
            placeholder="NDL-CR-…"
            className="font-mono text-xs"
          />
        </div>
        <div>
          <Label className="text-xs">Category</Label>
          <Select
            value={category}
            onValueChange={(v) => setCategory(v as any)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {(Object.keys(BUCKETS) as Bucket[]).map((b) => (
                <SelectItem key={b} value={b}>
                  {BUCKETS[b].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Events table */}
      <section className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Event type</th>
              <th className="px-3 py-2">Reference</th>
              <th className="px-3 py-2">External event id</th>
              <th className="px-3 py-2">Error / notes</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  No matching ITN events.
                </td>
              </tr>
            ) : (
              rows.map((r: any) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString("en-ZA")}
                  </td>
                  <td className="px-3 py-2">
                    <CategoryPill bucket={r._bucket} />
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.event_type}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {r.reference ?? "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    <div className="flex items-center gap-1">
                      <span className="max-w-[220px] truncate" title={r.external_event_id ?? ""}>
                        {r.external_event_id ?? "—"}
                      </span>
                      {r.external_event_id && (
                        <button
                          onClick={() =>
                            navigator.clipboard?.writeText(r.external_event_id)
                          }
                          title="Copy"
                          className="text-muted-foreground hover:text-navy"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-rose-700">
                    {r.error_message ?? (r.processed ? "—" : "")}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Page {page} of {totalPages} · {total} total PayFast events
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <aside className="rounded-xl border border-dashed border-border bg-muted/20 p-4 text-xs text-muted-foreground">
        <p className="font-semibold text-navy">How idempotency works</p>
        <p className="mt-1">
          Every PayFast callback writes a row keyed by{" "}
          <code>provider + external_event_id</code> (formatted as{" "}
          <code>pfPaymentId:status</code>). A second callback for the same
          payment lands as a unique-constraint violation and is recorded as
          a duplicate — credits are never granted twice. If you see
          "Duplicate" rows here it means PayFast retried and our protection
          worked.
        </p>
      </aside>
    </div>
  );
}
